// @ts-nocheck
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Tile,
  Form,
  TextInput,
  Button,
  Grid,
  Column,
  Stack,
  Heading,
  InlineNotification,
  ProgressIndicator,
  ProgressStep,
} from '@carbon/react';
import { Email, CheckmarkFilled, ArrowLeft, Security } from '@carbon/icons-react';

import { authService } from '../../services/authService';
import styles from './ForgotPasswordPage.module.scss';

interface ForgotPasswordPageProps {}

const ForgotPasswordPage: React.FC<ForgotPasswordPageProps> = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'email' | 'sent' | 'error'>('email');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Call the password reset service
      await authService.requestPasswordReset(email);

      setSuccessMessage(`Password reset instructions have been sent to ${email}`);
      setStep('sent');
    } catch (error: any) {
      setError(error.message || 'Failed to send password reset email. Please try again.');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const renderEmailStep = () => (
    <Form onSubmit={handleSubmit}>
      <Stack gap={5}>
        <div className={styles['text-center']}>
          <div className={`${styles['icon-circle']} ${styles.brand}`}>
            <Security size={24} className={styles['icon-on-color']} />
          </div>
          <Heading className={styles.heading}>Reset Your Password</Heading>
          <p className={styles.description}>
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        {error && <InlineNotification kind="error" title="Error" subtitle={error} lowContrast hideCloseButton />}

        <TextInput
          id="email"
          name="email"
          type="email"
          labelText="Email Address"
          placeholder="Enter your email address"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          disabled={loading}
          autoComplete="email"
          autoFocus
          aria-describedby="email-helper"
          helperText="We'll send reset instructions to this email address"
          renderIcon={() => <Email size={16} />}
        />

        <Button type="submit" size="lg" disabled={loading || !email} className={styles['full-width-button']}>
          {loading ? 'Sending...' : 'Send Reset Instructions'}
        </Button>

        <div className={styles['text-center']}>
          <Button
            kind="ghost"
            size="sm"
            onClick={handleBackToLogin}
            disabled={loading}
            renderIcon={() => <ArrowLeft size={16} />}
          >
            Back to Sign In
          </Button>
        </div>
      </Stack>
    </Form>
  );

  const renderSuccessStep = () => (
    <Stack gap={5}>
      <div className={styles['text-center']}>
        <div className={`${styles['icon-circle']} ${styles.success}`}>
          <CheckmarkFilled size={24} className={styles['icon-on-color']} />
        </div>
        <Heading className={styles.heading}>Check Your Email</Heading>
        <p className={styles.description}>{successMessage}</p>
      </div>

      <InlineNotification
        kind="success"
        title="Email Sent Successfully"
        subtitle="Please check your email and follow the instructions to reset your password. The link will expire in 1 hour."
        lowContrast
        hideCloseButton
      />

      <div className={styles['info-box']}>
        <p className={styles['info-text']}>
          <span className={styles['info-text-strong']}>Didn't receive the email?</span>
          <br />
          Check your spam folder or wait a few minutes for the email to arrive.
        </p>
      </div>

      <Button
        kind="secondary"
        size="lg"
        onClick={handleBackToLogin}
        className={styles['full-width-button']}
        renderIcon={() => <ArrowLeft size={16} />}
      >
        Return to Sign In
      </Button>

      <div className={styles['text-center']}>
        <Button
          kind="ghost"
          size="sm"
          onClick={() => {
            setStep('email');
            setEmail('');
            setError('');
            setSuccessMessage('');
          }}
        >
          Try a different email address
        </Button>
      </div>
    </Stack>
  );

  return (
    <div className={styles['page-container']}>
      <Grid narrow>
        <Column sm={4} md={6} lg={6} xlg={4} max={4}>
          <Tile className={styles['form-tile']}>
            {/* Progress Indicator */}
            <div className={styles['progress-container']}>
              <ProgressIndicator currentIndex={step === 'email' ? 0 : 1} spaceEqually>
                <ProgressStep
                  label="Enter Email"
                  description="Provide your email address"
                  complete={step !== 'email'}
                />
                <ProgressStep label="Check Email" description="Follow reset instructions" complete={step === 'sent'} />
              </ProgressIndicator>
            </div>

            {/* Content based on current step */}
            {step === 'email' && renderEmailStep()}
            {step === 'sent' && renderSuccessStep()}
            {step === 'error' && renderEmailStep()}

            {/* Footer Section */}
            <div className={styles.footer}>
              <p className={styles['footer-text']}>
                🔒 Secure password reset process
                <br />✅ FERPA & COPPA Compliant • 📧 Email verification required
              </p>
            </div>
          </Tile>
        </Column>
      </Grid>
    </div>
  );
};

export default ForgotPasswordPage;
